import React from 'react';
import { ImageOff } from 'lucide-react';
import { firearmEvidenceService } from '../../services/firearmEvidenceService';
import { FirearmPrimaryImage } from '../../types/domain';
import DismissibleError from '../DismissibleError';
import './FirearmMasterImagePanel.css';

interface FirearmMasterImagePanelProps {
  image?: FirearmPrimaryImage | null;
  firearmSerialNumber?: string;
}

const formatBytes = (size: number): string => {
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const FirearmMasterImagePanel: React.FC<FirearmMasterImagePanelProps> = ({ image, firearmSerialNumber }) => {
  const [previewUrl, setPreviewUrl] = React.useState('');
  const [error, setError] = React.useState('');
  const [loading, setLoading] = React.useState(false);

  React.useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const preview = async () => {
    if (!image) return;
    setLoading(true);
    setError('');
    try {
      const blob = await firearmEvidenceService.getPreview(image.id);
      setPreviewUrl((current) => {
        if (current) URL.revokeObjectURL(current);
        return URL.createObjectURL(blob);
      });
    } catch {
      setError('The firearm image could not be opened.');
    } finally {
      setLoading(false);
    }
  };

  const download = async () => {
    if (!image) return;
    setError('');
    try {
      const blob = await firearmEvidenceService.downloadEvidence(image.id);
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = objectUrl;
      anchor.download = image.original_name;
      anchor.click();
      URL.revokeObjectURL(objectUrl);
    } catch {
      setError('The firearm image could not be downloaded.');
    }
  };

  if (!image) {
    return (
      <section className="firearm-master-image-panel" aria-label={`Firearm image${firearmSerialNumber ? ` for ${firearmSerialNumber}` : ''}`}>
        <div className="firearm-master-image-header">
          <div><span>Registry media</span><h3>Firearm Image</h3></div>
          <span className="firearm-master-image-status">No image</span>
        </div>
        <div className="firearm-master-image-frame firearm-master-image-empty">
          <ImageOff size={28} aria-hidden="true" />
          <strong>No firearm image available</strong>
          <span>No registry image is stored for this firearm.</span>
        </div>
      </section>
    );
  }

  return (
    <section className="firearm-master-image-panel" aria-label={`Firearm image${firearmSerialNumber ? ` for ${firearmSerialNumber}` : ''}`}>
      <div className="firearm-master-image-header">
        <div><span>Registry media</span><h3>Firearm Image</h3></div>
        <span className="firearm-master-image-status firearm-master-image-status--available">Available</span>
      </div>
      {error && <DismissibleError message={error} onDismiss={() => setError('')} />}
      <div className="firearm-master-image-frame">
        {previewUrl
          ? <img src={previewUrl} alt={`Firearm ${firearmSerialNumber || ''}`.trim()} />
          : <div className="firearm-master-image-placeholder">Protected preview</div>}
      </div>
      <dl className="firearm-master-image-metadata">
        <div><dt>File</dt><dd title={image.original_name}>{image.original_name}</dd></div>
        <div><dt>Type / Size</dt><dd>{image.mime_type} · {formatBytes(image.file_size)}</dd></div>
        <div><dt>Uploaded by</dt><dd>{image.uploaded_by}</dd></div>
        <div><dt>Uploaded</dt><dd>{new Date(image.uploaded_at).toLocaleString()}</dd></div>
      </dl>
      <div className="firearm-master-image-actions">
        <button className="btn btn-secondary" type="button" onClick={preview} disabled={loading}>{loading ? 'Opening...' : 'Open protected preview'}</button>
        <button className="btn btn-secondary" type="button" onClick={download}>Download</button>
      </div>
    </section>
  );
};

export default FirearmMasterImagePanel;
